/* eslint-disable @typescript-eslint/no-explicit-any */
import { Box, Button } from '@mui/material';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { setAlert } from '../../features/slices/alertSlice';
import { saveLabMaster } from '../../features/slices/labDiagnosticSlice';
import { AppDispatch, RootState } from '../../features/store';
import HCInput from '../Input';
import HCModal from '../Modal';

interface AddLabServiceModalProps {
    isOpen: boolean;
    onClose: () => void;
    onCreated: (labServiceId: number) => void;
}

const AddLabServiceModal = ({
    isOpen,
    onClose,
    onCreated,
}: AddLabServiceModalProps) => {
    const dispatch = useDispatch<AppDispatch>();
    const { data: user } = useSelector((state: RootState) => state.auth);
    const [labServiceArea, setLabServiceArea] = useState('');
    const [labServiceSubArea, setLabServiceSubArea] = useState('');
    const [fees, setFees] = useState('');
    const [saving, setSaving] = useState(false);

    const reset = () => {
        setLabServiceArea('');
        setLabServiceSubArea('');
        setFees('');
        setSaving(false);
    };

    const handleClose = () => {
        reset();
        onClose();
    };

    const onSave = () => {
        if (!labServiceArea.trim() || !labServiceSubArea.trim()) {
            dispatch(
                setAlert({
                    message:
                        'Lab Service Area and Lab Service Sub Area are required',
                    severity: 'error',
                }),
            );
            return;
        }
        setSaving(true);
        dispatch(
            saveLabMaster({
                id: 0,
                labServiceArea: labServiceArea.trim(),
                labServiceSubArea: labServiceSubArea.trim(),
                fees: fees.trim() === '' ? null : Number(fees),
                createdBy: user?.UserID,
            }),
        ).then((res: any) => {
            setSaving(false);
            const payload = res?.payload;
            if (
                res?.meta?.requestStatus === 'fulfilled' &&
                payload?.responseCode === '200'
            ) {
                const newId =
                    payload?.result?.id ??
                    payload?.result?.Id ??
                    payload?.result;
                const labServiceId = Number(newId);
                if (!labServiceId || Number.isNaN(labServiceId)) {
                    dispatch(
                        setAlert({
                            message:
                                'Lab service saved but id was not returned',
                            severity: 'error',
                        }),
                    );
                    return;
                }
                dispatch(
                    setAlert({
                        message: 'Lab service added successfully',
                        severity: 'success',
                    }),
                );
                reset();
                onClose();
                onCreated(labServiceId);
                return;
            }
            const raw =
                payload?.responseMessage ||
                payload?.response?.data?.responseMessage ||
                '';
            const message = /already exists/i.test(String(raw))
                ? 'This lab service already exists in the global catalog. Please select it from the dropdown.'
                : raw;
            if (message) {
                dispatch(
                    setAlert({
                        message,
                        severity: 'error',
                    }),
                );
            } else {
                dispatch(
                    setAlert({
                        message:
                            'Unable to save lab service. Please try again or select an existing service from the dropdown.',
                        severity: 'error',
                    }),
                );
            }
        });
    };

    return (
        <HCModal
            isOpen={isOpen}
            onClose={handleClose}
            title="Add New Lab Service"
            size="sm"
        >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <HCInput
                    label="Lab Service Area"
                    name="labServiceArea"
                    value={labServiceArea}
                    onChange={(e) => setLabServiceArea(e.target.value)}
                    required
                    disabled={saving}
                    size="small"
                />
                <HCInput
                    label="Lab Service Sub Area"
                    name="labServiceSubArea"
                    value={labServiceSubArea}
                    onChange={(e) => setLabServiceSubArea(e.target.value)}
                    required
                    disabled={saving}
                    size="small"
                />
                <HCInput
                    label="Lab Service Fee"
                    name="fees"
                    type="number"
                    value={fees}
                    onChange={(e) => setFees(e.target.value)}
                    disabled={saving}
                    size="small"
                    min={0}
                    max={100000}
                />
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        gap: 1,
                        mt: 1,
                    }}
                >
                    <Button onClick={handleClose} disabled={saving}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={onSave}
                        disabled={saving}
                    >
                        Save
                    </Button>
                </Box>
            </Box>
        </HCModal>
    );
};

export default AddLabServiceModal;
